import { Model } from 'dva'
import { OntologyLanguage } from '../types'
import { OntologiesService, OrganisationsService } from '../../../Service'
import { IsObject } from '../../../Utils/helpers'
import { FailureHandler } from 'Utils/ApiHelper'

const OntologyLanguages: Model = {
  namespace: 'ontologyLanguages',
  state: {
    list: [],
  },
  effects: {
    *list(
      {
        id,
        useCache = true,
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        additionalParams = {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let ontologyLanguages = []
        if (additionalParams?.forCurrentFlowConfig === true) {
          const flowUuid = yield select((state: any) => {
            return state.flowLayout.flow.uuid
          })
          ontologyLanguages = yield call(
            OrganisationsService.organisationsFlowsOntologiesLanguagesList,
            {
              organisationUuid: additionalParams?.organisationUuid,
              flowUuid,
              ontologyPk: id,
            }
          )
        } else {
          ontologyLanguages = yield call(OntologiesService.ontologiesLanguagesList, {
            ontologyPk: id,
          })
        }
        yield put({ type: 'modifyList', payload: ontologyLanguages })
        onSuccess(ontologyLanguages)
      } catch (err: any) {
        FailureHandler('Error fetching Ontology', err, onFailure)
      }
      setLoading(false)
    },
    *retrieve(
      {
        ontologyId,
        id,
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        additionalParams = {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let ontologyLanguage = yield select((state: any) =>
          state.ontologyLanguages.list.find(
            (ontologyLanguage: OntologyLanguage) => ontologyLanguage.id === id
          )
        )
        if (!ontologyLanguage) {
          if (additionalParams?.forCurrentFlowConfig === true) {
            const flowUuid = yield select((state: any) => {
              return state.flowLayout.flow.uuid
            })
            ontologyLanguage = yield call(
              OrganisationsService.organisationsFlowsOntologiesLanguagesRetrieve,
              {
                organsiationUuid: additionalParams?.organisationUuid,
                flowUuid,
                id: id,
                ontologyPk: ontologyId,
              }
            )
          } else {
            ontologyLanguage = yield call(OntologiesService.ontologiesLanguagesRetrieve, {
              id: id,
              ontologyPk: ontologyId,
            })
          }
        }
        yield put({ type: 'modifyList', payload: ontologyLanguage })
        onSuccess(ontologyLanguage)
      } catch (err) {
        FailureHandler('Could not find the Ontology!', err, onFailure)
      }
      setLoading(false)
    },
    *create(
      {
        ontologyId,
        data = {},
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        additionalParams = {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let response = null
        if (additionalParams?.forCurrentFlowConfig === true) {
          const flowUuid = yield select((state: any) => {
            return state.flowLayout.flow.uuid
          })
          response = yield call(OrganisationsService.organisationsFlowsOntologiesLanguagesCreate, {
            organisationUuid: additionalParams?.organisationUuid,
            flowUuid,
            requestBody: {
              ...data,
              ontology: ontologyId,
            },
            ontologyPk: ontologyId,
          })
        } else {
          response = yield call(OntologiesService.ontologiesLanguagesCreate, {
            requestBody: {
              ...data,
              ontology: ontologyId,
            },
            ontologyPk: ontologyId,
          })
        }
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error adding a Ontology!', err, onFailure)
      }
      setLoading(false)
    },
    *partialUpdate(
      {
        id,
        ontologyId,
        data,
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        additionalParams = {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let response = null
        if (additionalParams?.forCurrentFlowConfig === true) {
          const flowUuid = yield select((state: any) => {
            return state.flowLayout.flow.uuid
          })
          response = yield call(
            OrganisationsService.organisationsFlowsOntologiesLanguagesPartialUpdate,
            {
              organisationUuid: additionalParams?.organisationUuid,
              flowUuid,
              id: id,
              ontologyPk: ontologyId,
              requestBody: data,
            }
          )
        } else {
          response = yield call(OntologiesService.ontologiesLanguagesPartialUpdate, {
            id: id,
            ontologyPk: ontologyId,
            requestBody: data,
          })
        }
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error editing a Ontology!', err, onFailure)
      }
      setLoading(false)
    },
    *destroy(
      {
        idParentAlias,
        id,
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        additionalParams,
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        if (additionalParams?.forCurrentFlowConfig === true) {
          const flowUuid = yield select((state: any) => {
            return state.flowLayout.flow.uuid
          })
          yield call(OrganisationsService.organisationsFlowsOntologiesLanguagesDestroy, {
            organisationUuid: additionalParams?.organisationUuid,
            flowUuid,
            id: id,
            ontologyPk: idParentAlias,
          })
        } else {
          yield call(OntologiesService.ontologiesLanguagesDestroy, {
            id: id,
            ontologyPk: idParentAlias,
          })
        }
        yield put({ type: 'modifyList', payload: id })
        onSuccess()
      } catch (err) {
        FailureHandler('Error deleting a Ontology', err, onFailure)
      }
      setLoading(false)
    },
  },
  reducers: {
    modifyList(state, action) {
      // clone the list
      let list = [...state.list]
      if (IsObject(action.payload)) {
        // if the payload is an object, it's a single object, so add it to the list
        list = list.filter((l: OntologyLanguage) => l.id !== action.payload.id)
        list.push(action.payload)
      } else if (Array.isArray(action.payload)) {
        // if the payload is an array, it's a list of objects, so add them to the list
        list = list
          .filter(
            (l: OntologyLanguage) =>
              !action.payload.map((p: OntologyLanguage) => p.id).includes(l.id)
          )
          .concat(action.payload)
      } else if (Number.isInteger(action.payload)) {
        // if the payload is an integer, it's an id sent in delete operations, so remove it from the list
        list = list.filter((l: OntologyLanguage) => l.id !== action.payload)
      }
      return {
        ...state,
        list: list,
      }
    },
    resetOntologyLanguages(state, action) {
      return {
        ...state,
        list: [],
      }
    },
  },
}

export default OntologyLanguages
